import { useMutationHook, useQueryHook } from "@/hooks"
import { TEAM_SETTING_CACHE } from "@/hooks/api/queryCache"
import {
  createPlayerGroup,
  createSeasonWeek,
  matchSeasonWeekActivities,
  deletePlayerGroup,
  readACWR,
  readCalculateUnit,
  readRunAction,
  readAccelAction,
  readDecelAction,
  readJumpAction,
  readDiveAction,
  readCODAction,
  readGroupPlayers,
  readMetricGroup,
  readMetricGroups,
  readOtherPlayers,
  readPlayerGroups,
  readPlayers,
  readSeasonWeek,
  readUnit,
  readUnits,
  upsertRunAction,
  upsertAccelAction,
  upsertDecelAction,
  upsertJumpAction,
  upsertDiveAction,
  upsertCODAction,
  upsertACWR,
  upsertMetricGroup,
  upsertPlayerGroup,
  upsertUnit,
  readCalculatedMetrics,
  createCalculatedMetrics,
  updateCalculatedMetrics,
  deleteCalculatedMetrics,
} from "@/app/api/setting"
import {
  ReadActionsResponse,
  ReadCalculateUnitParams,
  ReadGroupPlayersResponse,
  ReadPlayersResponse,
} from "@/types/setting"
import { UseQueryOptions } from "@tanstack/react-query"
import { ApiError } from "@/app/api/type"
import { isUndefined } from "lodash"

/* Unit */
export const useUnitsQuery = () => {
  return useQueryHook({ queryKey: ["readUnits"], queryFn: readUnits })
}

export const useUnitQuery = () => {
  return useQueryHook({ queryKey: ["readUnit"], queryFn: readUnit })
}

export const useCalculateUnitQuery = (params: ReadCalculateUnitParams) => {
  return useQueryHook({
    queryKey: ["readCalculateUnit", params.type, params.value],
    queryFn: () => readCalculateUnit(params),
  })
}

export const useUpsertUnitMutation = () => {
  return useMutationHook(upsertUnit, ["readUnit", "readUnits"])
}

/* Advanced */
export const useReadACWRQuery = () => {
  return useQueryHook({ queryKey: ["readACWR"], queryFn: readACWR })
}

export const useSeasonWeekQuery = () => {
  return useQueryHook({ queryKey: ["readSessionWeek"], queryFn: readSeasonWeek })
}

export const useUpsertACWRMutation = () => {
  return useMutationHook(upsertACWR, ["readACWR"])
}

export const useCreateSeasonWeekMutation = () => {
  return useMutationHook(createSeasonWeek, ["readSessionWeek"])
}

export const useMatchSeasonWeekActivitiesMutation = () => {
  return useMutationHook(matchSeasonWeekActivities)
}

/* Metric Group */
export const useMetricGroupsQuery = () => {
  return useQueryHook({ queryKey: ["readMetricGroups"], queryFn: readMetricGroups })
}

export const useMetricGroupQuery = () => {
  return useQueryHook({ queryKey: ["readMetricGroup"], queryFn: readMetricGroup })
}

export const useUpsertMetricGroupMutation = () => {
  return useMutationHook(upsertMetricGroup, ["readMetricGroup"])
}

/* Team Player Group */
export const usePlayerGroupsQuery = () => {
  return useQueryHook({
    queryKey: ["readPlayerGroups"],
    queryFn: readPlayerGroups,
  })
}

export const usePlayersQuery = () => {
  return useQueryHook<ReadPlayersResponse, unknown>({
    queryKey: ["readPlayers"],
    queryFn: readPlayers,
  })
}

export const useOtherPlayersQuery = (idfTeam?: number) => {
  return useQueryHook<ReadPlayersResponse, unknown>({
    queryKey: ["readPlayers", idfTeam],
    queryFn: () => readOtherPlayers(idfTeam!),
    options: { enabled: !isUndefined(idfTeam) } as UseQueryOptions<ReadPlayersResponse, ApiError>,
  })
}

export const useGroupPlayersQuery = (idfTeamPlayerGroup?: number) => {
  return useQueryHook<ReadGroupPlayersResponse, { idfTeamPlayerGroup?: number }>({
    queryKey: ["readGroupPlayers", idfTeamPlayerGroup],
    queryFn: () => readGroupPlayers(idfTeamPlayerGroup!),
    options: { enabled: !isUndefined(idfTeamPlayerGroup) } as UseQueryOptions<
      ReadGroupPlayersResponse,
      ApiError
    >,
  })
}

export const useCreatePlayerGroupMutation = () => {
  return useMutationHook(createPlayerGroup, ["readPlayerGroups"])
}

export const useUpsertPlayerGroupMutation = () => {
  return useMutationHook(upsertPlayerGroup, ["readPlayerGroups"])
}

export const useDeletePlayerGroupMutation = () => {
  return useMutationHook(deletePlayerGroup, ["readPlayerGroups"])
}

/* Action */
export const useRunActionQuery = ({ enabled }: { enabled: boolean }) => {
  return useQueryHook({
    queryKey: ["readRunAction"],
    queryFn: readRunAction,
    options: { ...TEAM_SETTING_CACHE, enabled } as UseQueryOptions<ReadActionsResponse, ApiError>,
  })
}
export const useAccelActionQuery = ({ enabled }: { enabled: boolean }) => {
  return useQueryHook({
    queryKey: ["readAccelAction"],
    queryFn: readAccelAction,
    options: { ...TEAM_SETTING_CACHE, enabled } as UseQueryOptions<ReadActionsResponse, ApiError>,
  })
}
export const useDecelActionQuery = ({ enabled }: { enabled: boolean }) => {
  return useQueryHook({
    queryKey: ["readDecelAction"],
    queryFn: readDecelAction,
    options: { ...TEAM_SETTING_CACHE, enabled } as UseQueryOptions<ReadActionsResponse, ApiError>,
  })
}
export const useJumpActionQuery = () => {
  return useQueryHook({
    queryKey: ["readJumpAction"],
    queryFn: readJumpAction,
    options: TEAM_SETTING_CACHE,
  })
}
export const useDiveActionQuery = () => {
  return useQueryHook({
    queryKey: ["readDiveAction"],
    queryFn: readDiveAction,
    options: TEAM_SETTING_CACHE,
  })
}
export const useCODActionQuery = () => {
  return useQueryHook({
    queryKey: ["readCODAction"],
    queryFn: readCODAction,
    options: TEAM_SETTING_CACHE,
  })
}

export const useUpsertRunActionMutation = () => {
  return useMutationHook(upsertRunAction, ["readRunAction"])
}
export const useUpsertAccelActionMutation = () => {
  return useMutationHook(upsertAccelAction, ["readAccelAction"])
}
export const useUpsertDecelActionMutation = () => {
  return useMutationHook(upsertDecelAction, ["readDecelAction"])
}
export const useUpsertJumpActionMutation = () => {
  return useMutationHook(upsertJumpAction, ["readJumpAction"])
}
export const useUpsertDiveActionMutation = () => {
  return useMutationHook(upsertDiveAction, ["readDiveAction"])
}
export const useUpsertCODActionMutation = () => {
  return useMutationHook(upsertCODAction, ["readCODAction"])
}

export const useCalculatedMetricsQuery = () => {
  return useQueryHook({ queryKey: ["readCalculatedMetrics"], queryFn: readCalculatedMetrics })
}

export const useCreateCalculatedMetricsMutation = () => {
  return useMutationHook(createCalculatedMetrics, ["readCalculatedMetrics", "readMetricList"])
}

export const useUpdateCalculatedMetricsMutation = () => {
  return useMutationHook(updateCalculatedMetrics, ["readCalculatedMetrics", "readMetricList"])
}

export const useDeleteCalculatedMetricsMutation = () => {
  return useMutationHook(deleteCalculatedMetrics, ["readCalculatedMetrics", "readMetricList"])
}
